import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
  Button,
  Alert,
} from 'reactstrap';

/**
 * Schema-getriebenes Edit-Modal für einen Integration-Provider.
 *
 * Spec: lmm-wiki/wiki/orbit-specs/admin-integrations-and-settings-shapes.md
 *
 * Felder kommen aus `item.configSchema` (vom Server geliefert), nicht
 * client-seitig hardcoded. Pro Field-Type wird der passende Input gerendert:
 *  - 'string' / 'number' / 'url'  → Input
 *  - 'boolean'                    → Switch
 *  - 'enum:val1,val2,...'         → Select
 *  - secret: true                 → Password-Input mit Preserve-Semantik
 *
 * Per Spec A3: leeres Secret-Feld = "Wert preserve". Non-Secret leeres Feld
 * = clear (Server validiert required dann).
 *
 * Initial-Form-Values:
 *  - Non-Secret-Felder werden mit der Preview-Klartextwert gefüllt
 *  - Secret-Felder bleiben leer (heißt "preserve" beim Submit)
 *  - Boolean: aus Preview, sonst false
 */
export function IntegrationEditModal({ item, onSubmit, onClose, busy, canWrite }) {
  const [config, setConfig] = useState(() => buildInitial(item));
  const [notes, setNotes] = useState(item.notes ?? '');
  const [error, setError] = useState(null);

  const onChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    const missing = item.configSchema
      .filter((f) => f.required && !f.secret)
      .filter((f) => isEmptyValue(config[f.key], f.type))
      .map((f) => f.label);
    if (missing.length > 0) {
      setError(`Pflichtfelder fehlen: ${missing.join(', ')}`);
      return;
    }
    try {
      await onSubmit(config, notes.trim() || null);
    } catch (err) {
      setError(err?.data?.message ?? err?.message ?? 'Speichern fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen toggle={busy ? undefined : onClose} size="lg" backdrop="static">
      <ModalHeader toggle={busy ? undefined : onClose}>
        {item.displayName}
      </ModalHeader>
      <Form onSubmit={onSubmitForm}>
        <ModalBody>
          <p className="small text-muted">{item.description}</p>

          {item.configSchema.map((field) => (
            <SchemaField
              key={field.key}
              field={field}
              value={config[field.key]}
              previewValue={item.preview?.[field.key]}
              hasSecretConfigured={
                field.secret && item.preview?.[field.key] != null
              }
              onChange={(v) => onChange(field.key, v)}
              disabled={!canWrite || busy}
            />
          ))}

          <FormGroup className="mb-0 mt-3 pt-3 border-top">
            <Label className="small text-muted">Notes (max 500 Zeichen, optional)</Label>
            <Input
              type="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 500))}
              disabled={!canWrite || busy}
              placeholder="z. B. Account-Owner, Renewal-Datum, Production-Hinweis…"
            />
          </FormGroup>

          {error && <Alert color="danger" className="mt-3 mb-0 small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={busy || !canWrite}>
            {busy ? 'Speichern…' : 'Speichern'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function buildInitial(item) {
  const out = {};
  for (const field of item.configSchema) {
    if (field.secret) {
      out[field.key] = '';
      continue;
    }
    const preview = item.preview?.[field.key];
    if (field.type === 'boolean') {
      out[field.key] = preview === true || preview === 'true';
    } else if (field.type === 'number') {
      out[field.key] = preview != null ? Number(preview) : '';
    } else {
      out[field.key] = preview != null ? String(preview) : '';
    }
  }
  return out;
}

function isEmptyValue(value, type) {
  if (value == null) return true;
  if (type === 'boolean') return false; // boolean always "filled"
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return Number.isNaN(value);
  return false;
}

function parseEnum(type) {
  // 'enum:sandbox,live' → ['sandbox', 'live']
  if (!type.startsWith('enum:')) return [];
  return type.slice('enum:'.length).split(',').map((s) => s.trim()).filter(Boolean);
}

function SchemaField({ field, value, previewValue, hasSecretConfigured, onChange, disabled }) {
  const enumValues = field.type.startsWith('enum:') ? parseEnum(field.type) : null;

  return (
    <FormGroup className="mb-3 pb-2 border-bottom">
      <Label className="d-flex justify-content-between align-items-start mb-1">
        <span className="fw-semibold">
          {field.label}
          {field.required && <span className="text-danger ms-1">*</span>}
          {field.secret && (
            <Badge color="warning" pill className="ms-2" style={{ fontSize: 10 }}>
              Secret
            </Badge>
          )}
        </span>
        <code className="small text-muted">{field.key}</code>
      </Label>

      {field.type === 'boolean' && (
        <div className="form-check form-switch">
          <Input
            type="switch"
            role="switch"
            id={`int-edit-${field.key}`}
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
          <Label check for={`int-edit-${field.key}`} className="ms-2">
            {value ? 'an' : 'aus'}
          </Label>
        </div>
      )}

      {enumValues && (
        <Input
          type="select"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          bsSize="sm"
        >
          <option value="">— wählen —</option>
          {enumValues.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </Input>
      )}

      {field.type === 'number' && (
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') onChange('');
            else onChange(Number(raw));
          }}
          placeholder={field.placeholder}
          disabled={disabled}
          bsSize="sm"
        />
      )}

      {field.secret && (
        <InputGroup size="sm">
          <Input
            type="password"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={
              hasSecretConfigured
                ? `${previewValue} — leer lassen zum Erhalten, neuer Wert überschreibt`
                : field.placeholder ?? 'Neuen Wert eingeben'
            }
            disabled={disabled}
            autoComplete="new-password"
          />
          {hasSecretConfigured && (
            <InputGroupText title="Bereits konfiguriert">●</InputGroupText>
          )}
        </InputGroup>
      )}

      {!field.secret && !enumValues && field.type !== 'boolean' && field.type !== 'number' && (
        <Input
          type={field.type === 'url' ? 'url' : 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          bsSize="sm"
        />
      )}
    </FormGroup>
  );
}
